import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from './AppIcon';
import { getEscrowById } from '../lib/adminApi';

const statusStyles = {
  completed: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-rose-100 text-rose-700',
  processing: 'bg-blue-100 text-blue-700',
  escrowed: 'bg-blue-100 text-blue-700',
  released: 'bg-emerald-100 text-emerald-700',
};

export default function PaymentDetailModal({ paymentId, onClose }) {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!paymentId) return;
    setLoading(true);
    setError(null);
    getEscrowById(paymentId)
      .then(setPayment)
      .catch((e) => setError(e?.message ?? 'Failed to load payment'))
      .finally(() => setLoading(false));
  }, [paymentId]);

  if (!paymentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-card max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-xl shadow-xl border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            Payment details {paymentId ? `· ${String(paymentId).slice(0, 8)}…` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-4rem)] px-6 py-4 space-y-4">
          {loading && (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          )}
          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          )}
          {!loading && !error && payment && (
            <>
              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Payment</h3>
                <div className="grid gap-2 rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reference</span>
                    <span className="font-mono text-foreground">{payment.payment_reference ?? '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-semibold text-foreground">R{Number(payment.gross_amount ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[payment.payment_status] ?? 'bg-muted'}`}>
                      {payment.payment_status ?? '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Method</span>
                    <span className="text-foreground">{payment.payment_method ?? '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span className="text-foreground">{payment.created_at ? format(new Date(payment.created_at), 'dd MMM yyyy HH:mm') : '—'}</span>
                  </div>
                  {payment.escrowed_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Escrowed at</span>
                      <span className="text-foreground">{format(new Date(payment.escrowed_at), 'dd MMM yyyy HH:mm')}</span>
                    </div>
                  )}
                </div>
              </section>
              {payment.escrow_status && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Escrow</h3>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[payment.escrow_status] ?? 'bg-muted'}`}>
                      {payment.escrow_status}
                    </span>
                    {payment.commission_amount != null && (
                      <span className="text-muted-foreground">Commission: R{Number(payment.commission_amount).toLocaleString()}</span>
                    )}
                    {payment.caterer_net_amount != null && (
                      <span className="text-foreground font-medium">Caterer net: R{Number(payment.caterer_net_amount).toLocaleString()}</span>
                    )}
                  </div>
                </section>
              )}
              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Parties</h3>
                <div className="grid gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Customer </span>
                    <span className="text-foreground">{payment.customer?.email ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Caterer </span>
                    <span className="text-foreground">{payment.caterer?.business_name ?? '—'}</span>
                  </div>
                  {payment.order && (
                    <div>
                      <span className="text-muted-foreground">Order </span>
                      <span className="font-mono text-foreground">{payment.order.id?.slice(0, 8)}…</span>
                      {payment.order.total_amount != null && (
                        <span className="text-muted-foreground"> · R{Number(payment.order.total_amount).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
